"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getFaClientNarrativeSource, saveFaClientNarrativeSource } from "@/services/api/agentic-v2";
import { clientsKeys } from "@/services/api/agentic-v2/queries/keys";
import { useCompanyId } from "@/context/CompanyContext";
import {
  NarrativeSourcePicker,
  type NarrativeSourcePanelData,
} from "@/components/review/definition/steps/NarrativeSourcePicker";
import { LoadingPlaceholder } from "@/components/ui/loading-placeholder";

export function ClientNarrativeSourcePanel({
  faClientId,
  missionId,
  className,
}: {
  faClientId: string;
  missionId?: string | null;
  className?: string;
}) {
  const queryClient = useQueryClient();
  const companyId = useCompanyId();
  const queryKey = clientsKeys.narrativeSource(faClientId, missionId ?? null, companyId ?? undefined);

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => getFaClientNarrativeSource(faClientId, missionId ?? undefined),
    enabled: Boolean(faClientId),
  });

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      saveFaClientNarrativeSource(faClientId, {
        ...values,
        ...(values.scope === "mission" && missionId ? { mission_id: missionId } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: clientsKeys.detail(faClientId) });
    },
  });

  if (isLoading) {
    return <LoadingPlaceholder height="120px" className={className} />;
  }
  if (isError || !data) {
    return null;
  }

  const panel = data as NarrativeSourcePanelData;
  const hasMission = Boolean(missionId) || panel.has_mission;

  return (
    <NarrativeSourcePicker
      panel={{ ...panel, has_mission: hasMission }}
      className={className}
      onSave={(values) => save.mutateAsync(values)}
      saving={save.isPending}
    />
  );
}
